import { Box, Typography } from '@mui/material';
import {
  DataGrid,
  DataGridProps,
  GridColDef,
  GridRowsProp,
} from '@mui/x-data-grid';
import React from 'react';

const DataTable: React.FC<
  DataGridProps & {
    rows: GridRowsProp;
    columns: GridColDef[];
    noResultsMessage?: string | React.ReactNode;
    hoverStyling?: boolean;
    autoHeight?: boolean;
    noShadow?: boolean;
  }
> = ({
  rows,
  columns,
  noResultsMessage = 'No results',
  hoverStyling = false,
  autoHeight = true,
  noShadow = false,
  pageSizeOptions = [20, 50], // default if not passed
  ...props
}) => {
  const noResultsMessageIsString = typeof noResultsMessage === 'string';
  const noResultsMessagingString = (
    <Typography
      display="flex"
      height="100%"
      alignItems="center"
      justifyContent="center"
    >
      {noResultsMessage}
    </Typography>
  );
  const noResultsMessagingElement = <>{noResultsMessage}</>;

  // Derive the "lowest page size" from pageSizeOptions[0]
  const lowestPageSize = pageSizeOptions[0] || 20;

  return (
    <Box sx={{ width: '100%' }}>
      <DataGrid
        rows={rows}
        columns={columns}
        autoHeight={autoHeight}
        rowHeight={44}
        columnHeaderHeight={48}
        disableRowSelectionOnClick
        initialState={{
          pagination: {
            paginationModel: {
              // Default page size can still be overridden via props.initialState
              pageSize: lowestPageSize,
            },
          },
          ...props.initialState,
        }}
        pageSizeOptions={pageSizeOptions}
        // Hide pagination if total rows <= the smallest page size
        hideFooterPagination={rows.length <= lowestPageSize}
        slots={{
          noRowsOverlay: () =>
            noResultsMessageIsString
              ? noResultsMessagingString
              : noResultsMessagingElement,
        }}
        {...props}
        sx={{
          background: 'white',
          boxShadow: noShadow ? '0 4px 8px rgba(0, 0, 0, 0.05)' : 'none',
          fontSize: '0.875rem',
          '& .MuiPaper-root': {
            borderRadius: '8px',
          },
          '& .MuiDataGrid-row': {
            cursor: 'default',
          },
          '& .MuiDataGrid-columnHeaders': {
            bgcolor: 'grey.100',
          },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-footerContainer': {
            height: 42,
            minHeight: 42,
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 700,
          },
          '& .MuiDataGrid-columnHeader': {
            bgcolor: 'grey.100',
            color: '#000',
          },
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:active':
            {
              outline: 'none',
            },
          ...(!hoverStyling
            ? {
                '& .MuiDataGrid-row:hover': {
                  background: 'transparent',
                },
              }
            : {}),
          ...props.sx,
        }}
      />
    </Box>
  );
};

export default DataTable;
